import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { LockKeyhole, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const authConfig = () => {
  const token = localStorage.getItem('admin_token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const labels = {
  clients: 'Clienți',
  customer_accounts: 'Conturi clienți'
};

const CustomerAccessGate = ({ area, children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadAccess = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/customer-access/my`,
        authConfig()
      );

      const entries = Array.isArray(response.data)
        ? response.data
        : [];

      const entry = entries.find(
        (item) => item.access_area === area
      );

      setStatus(entry?.status || 'not_requested');
    } catch (error) {
      setStatus('error');
      toast.error('Nu am putut verifica permisiunile.');
    } finally {
      setLoading(false);
    }
  }, [area]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const requestAccess = async () => {
    if (sending) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/api/admin/customer-access/request/${area}`,
        {},
        authConfig()
      );

      toast.success('Cererea a fost trimisa developerului.');
      await loadAccess();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Nu am putut trimite cererea.'
      );
    } finally {
      setSending(false);
    }
  };

  if (user?.role === 'developer') {
    return children;
  }

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-400">
        Se verifica permisiunile...
      </div>
    );
  }

  if (status === 'approved') {
    return children;
  }

  const pending = status === 'pending';
  const error = status === 'error';

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-8 text-center text-white">
        <LockKeyhole
          size={42}
          className="mx-auto mb-5 text-amber-400"
        />

        <h2 className="text-2xl font-bold mb-3">
          Acces restricționat
        </h2>

        <p className="text-slate-400 mb-6">
          Pentru secțiunea „{labels[area] || area}”
          ai nevoie de aprobarea developerului.
        </p>

        {pending && (
          <p className="mb-5 text-amber-300">
            Cererea ta este în așteptarea aprobării.
          </p>
        )}

        <button
          type="button"
          disabled={sending}
          onClick={error || pending ? loadAccess : requestAccess}
          className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-3 font-semibold text-slate-950 hover:bg-amber-300 disabled:opacity-50"
        >
          {sending ? (
            <RefreshCw size={18} className="animate-spin" />
          ) : error || pending ? (
            <RefreshCw size={18} />
          ) : (
            <Send size={18} />
          )}

          {error || pending
            ? 'Verifică din nou'
            : 'Solicită acces'}
        </button>
      </div>
    </div>
  );
};

export default CustomerAccessGate;
